<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
-->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('') }}
        </oxd-text>
        <oxd-switch-input
          v-model="editable"
          :option-label="$t('general.edit')"
          label-position="left"
        />
      </div>

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-divider />
        <div class="orangehrm-header-container">
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ $t('Tax Year') }}
          </oxd-text>
        </div>
        <oxd-divider />

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.taxYear"
                :label="$t('Tax Year (YYYY)')"
                :rules="rules.taxYear"
                :disabled="!editable"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-divider />
          <div class="orangehrm-header-container">
            <oxd-text tag="h6" class="orangehrm-main-title">
              {{ $t('Payroll Taxes') }}
            </oxd-text>
          </div>
          <oxd-divider />
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.federalTax"
                :label="$t('pim.federal_tax')"
                :rules="rules.federalTax"
                :disabled="!editable"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.socialSecurity"
                :label="$t('pim.social_security')"
                :rules="rules.socialSecurity"
                :disabled="!editable"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.medicare"
                :label="$t('pim.medicare')"
                :rules="rules.medicare"
                :disabled="!editable"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />
        <div class="orangehrm-header-container">
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ $t('State Unemployment Taxes') }}
          </oxd-text>
        </div>
        <oxd-divider />

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.sutaRate"
                :label="$t('SUTA Rate')"
                :rules="rules.rate"
                :disabled="!editable"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.sutaBase"
                :label="$t('SUTA Wage Base')"
                :rules="rules.amount"
                :disabled="!editable"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />
        <div class="orangehrm-header-container">
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ $t('Standard Deductions') }}
          </oxd-text>
        </div>
        <oxd-divider />

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.standardDeductionSingle"
                :label="$t('Standard Deduction Single')"
                :rules="rules.amount"
                :disabled="!editable"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.standardDeductionMarriedJoint"
                :label="$t('Standard Deduction Married Joint')"
                :rules="rules.amount"
                :disabled="!editable"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.standardDeductionMarriedSeparate"
                :label="$t('Standard Deduction Married Separate')"
                :rules="rules.amount"
                :disabled="!editable"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="deductions.standardDeductionHeadOfHousehold"
                :label="$t('Standard Deduction Head of Household')"
                :rules="rules.amount"
                :disabled="!editable"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />
        <div class="orangehrm-header-container">
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ $t('Federal Tax Brackets') }}
          </oxd-text>
        </div>
        <oxd-divider />

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="deductions-native-field">
                <label class="deductions-native-label">
                  {{ $t('Filing Status') }}
                </label>
                <select
                  v-model="selectedStatus"
                  class="deductions-native-select"
                  :disabled="!editable"
                >
                  <option value="SINGLE">Single</option>
                  <option value="MARRIED_JOINT">Married Joint</option>
                  <option value="MARRIED_SEPARATE">Married Separate</option>
                  <option value="HEAD_OF_HOUSEHOLD">Head of Household</option>
                </select>
              </div>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <div class="deductions-brackets-section">
            <table class="deductions-brackets-table">
              <thead>
                <tr>
                  <th>{{ $t('Rate (in decimals)') }}</th>
                  <th>{{ $t('Up To') }}</th>
                  <th>{{ $t('Actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(bracket, index) in currentBrackets"
                  :key="`${selectedStatus}-${index}`"
                >
                  <td>
                    <input
                      v-model="bracket.rate"
                      type="number"
                      step="0.01"
                      min="0"
                      class="deductions-brackets-input"
                      :disabled="!editable"
                    />
                  </td>
                  <td>
                    <input
                      v-if="bracket.up_to !== null"
                      v-model="bracket.up_to"
                      type="number"
                      step="100"
                      min="0"
                      class="deductions-brackets-input"
                      :disabled="!editable"
                    />
                    <span v-else class="deductions-no-limit-text">
                      {{ $t('No Limit') }}
                    </span>
                  </td>
                  <td class="deductions-brackets-actions">
                    <button
                      type="button"
                      class="oxd-button oxd-button--medium"
                      @click="toggleNoLimit(index)"
                      :disabled="!editable"
                    >
                      {{
                        bracket.up_to === null
                          ? $t('Set Value')
                          : $t('No Limit')
                      }}
                    </button>
                    <button
                      type="button"
                      class="oxd-button oxd-button--medium oxd-button--ghost"
                      @click="removeBracket(index)"
                      :disabled="!editable"
                    >
                      {{ $t('Delete') }}
                    </button>
                  </td>
                </tr>
                <tr v-if="!currentBrackets.length">
                  <td colspan="3" class="deductions-empty-row">
                    {{ $t('No brackets added for this filing status') }}
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="deductions-brackets-footer">
              <button
                type="button"
                class="oxd-button oxd-button--medium"
                @click="addBracket"
                :disabled="!editable"
              >
                {{ $t('Add Bracket') }}
              </button>
            </div>
          </div>
        </oxd-form-row>

        <oxd-form-actions>
          <required-text />
          <submit-button v-if="editable" />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import {shouldNotExceedCharLength} from '@ohrm/core/util/validation/rules';
import {OxdSwitchInput} from '@ohrm/oxd';

const defaultFederalBrackets = () => ({
  SINGLE: [],
  MARRIED_JOINT: [],
  MARRIED_SEPARATE: [],
  HEAD_OF_HOUSEHOLD: [],
});

export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/deductions',
    );

    return {
      http,
    };
  },

  data() {
    return {
      editable: false,
      isLoading: false,
      selectedStatus: 'SINGLE',
      deductions: {
        federalTax: '',
        socialSecurity: '',
        medicare: '',
        taxYear: '',
        sutaRate: '',
        sutaBase: '',
        standardDeductionSingle: '',
        standardDeductionMarriedJoint: '',
        standardDeductionMarriedSeparate: '',
        standardDeductionHeadOfHousehold: '',
        federalBrackets: defaultFederalBrackets(),
      },
      rules: {
        federalTax: [shouldNotExceedCharLength(10)],
        medicare: [shouldNotExceedCharLength(10)],
        socialSecurity: [shouldNotExceedCharLength(10)],
        taxYear: [shouldNotExceedCharLength(4)],
        rate: [shouldNotExceedCharLength(10)],
        amount: [shouldNotExceedCharLength(12)],
      },
      errors: [],
    };
  },

  computed: {
    currentBrackets() {
      return this.deductions.federalBrackets[this.selectedStatus] || [];
    },
  },

  created() {
    this.isLoading = true;
    this.http.http
      .get('/api/v2/pim/deductions')
      .then((response) => {
        const {data} = response.data;

        this.deductions.federalTax = data.federalTax ?? '';
        this.deductions.socialSecurity = data.socialSecurity ?? '';
        this.deductions.sutaRate = data.sutaRate ?? '';
        this.deductions.sutaBase = data.sutaBase ?? '';
        this.deductions.medicare = data.medicare ?? '';
        this.deductions.taxYear = data.taxYear ?? data.taxyear ?? '';
        this.deductions.standardDeductionSingle =
          data.standardDeductionSingle ?? '';
        this.deductions.standardDeductionMarriedJoint =
          data.standardDeductionMarriedJoint ?? '';
        this.deductions.standardDeductionMarriedSeparate =
          data.standardDeductionMarriedSeparate ?? '';
        this.deductions.standardDeductionHeadOfHousehold =
          data.standardDeductionHeadOfHousehold ?? '';

        this.deductions.federalBrackets = this.normalizeFederalBrackets(
          data.federalBrackets,
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    normalizeFederalBrackets(brackets) {
      const normalized = defaultFederalBrackets();

      if (!brackets || typeof brackets !== 'object') {
        return normalized;
      }

      Object.keys(normalized).forEach((status) => {
        if (Array.isArray(brackets[status])) {
          normalized[status] = brackets[status].map((item) => ({
            rate: item?.rate ?? '',
            up_to: Object.prototype.hasOwnProperty.call(item ?? {}, 'up_to')
              ? item.up_to
              : '',
          }));
        }
      });

      return normalized;
    },

    addBracket() {
      this.deductions.federalBrackets[this.selectedStatus].push({
        rate: '',
        up_to: '',
      });
    },

    removeBracket(index) {
      this.deductions.federalBrackets[this.selectedStatus].splice(index, 1);
    },

    toggleNoLimit(index) {
      const bracket =
        this.deductions.federalBrackets[this.selectedStatus][index];
      bracket.up_to = bracket.up_to === null ? '' : null;
    },

    buildFederalBracketsPayload() {
      const payload = {};

      Object.keys(this.deductions.federalBrackets).forEach((status) => {
        payload[status] = this.deductions.federalBrackets[status].map(
          (bracket) => ({
            rate:
              bracket.rate === '' || bracket.rate === null
                ? ''
                : Number(bracket.rate),
            up_to:
              bracket.up_to === '' || bracket.up_to === undefined
                ? ''
                : bracket.up_to === null
                ? null
                : Number(bracket.up_to),
          }),
        );
      });

      return payload;
    },

    onSave() {
      this.isLoading = true;
      //console.log(this.buildFederalBracketsPayload());

      this.http.http
        .put('/api/v2/pim/deductions', {
          federalTax: this.deductions.federalTax,
          socialSecurity: this.deductions.socialSecurity,
          sutaRate: this.deductions.sutaRate,
          sutaBase: this.deductions.sutaBase,
          medicare: this.deductions.medicare,
          taxYear: this.deductions.taxYear,
          standardDeductionSingle: this.deductions.standardDeductionSingle,
          standardDeductionMarriedJoint:
            this.deductions.standardDeductionMarriedJoint,
          standardDeductionMarriedSeparate:
            this.deductions.standardDeductionMarriedSeparate,
          standardDeductionHeadOfHousehold:
            this.deductions.standardDeductionHeadOfHousehold,
          federalBrackets: JSON.stringify(this.buildFederalBracketsPayload()),
        })
        .then(() => this.$toast.updateSuccess())
        .then(() => {
          this.editable = false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style src="./deductions.scss" lang="scss" scoped></style>

<style lang="scss" scoped>
.deductions-native-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.deductions-native-label {
  font-size: 0.875rem;
}

.deductions-native-select {
  min-height: 44px;
  padding: 0 0.75rem;
  border: 1px solid #d8dee6;
  border-radius: 0.5rem;
  background-color: #fff;
}

.deductions-brackets-section {
  width: 100%;
}

.deductions-brackets-table {
  width: 100%;
  border-collapse: collapse;
}

.deductions-brackets-table th,
.deductions-brackets-table td {
  padding: 0.75rem;
  border: 1px solid #e8eaef;
  text-align: left;
  vertical-align: middle;
}

.deductions-brackets-input {
  width: 80%;
  min-height: 40px;
  padding: 0 0.75rem;
  border: 1px solid #d8dee6;
  border-radius: 0.5rem;
}

.deductions-brackets-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.deductions-brackets-footer {
  margin-top: 1rem;
}

.deductions-empty-row {
  text-align: center;
  color: #64728c;
}

.deductions-no-limit-text {
  color: #64728c;
}
</style>
